import React, { useState, useMemo, useEffect } from "react";
import { FaSearch, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import "./Dashboard.css";
import { apiFetch } from "../api_call";

export default function Activity() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 8;

  const [searchTerm, setSearchTerm] = useState("");

  /* ---------- debounce search ---------- */
  const [debouncedTerm, setDebouncedTerm] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  /* ---------- auth ---------- */
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) navigate("/", { replace: true });
  }, [navigate]);

  /* ---------- fetch activity ---------- */
 useEffect(() => {
  async function fetchLogs() {
    try {
      setLoading(true);

      const token = localStorage.getItem("access_token");
      if (!token) return;

      const res = await apiFetch("/activity_log/", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res?.logs) {
        setData(
          res.logs.map((a) => ({
            department: a.department ?? "",
            email: a.email ?? "",
            date: a.datetime
              ? new Date(a.datetime).toLocaleDateString()
              : "",
            time: a.datetime
              ? new Date(a.datetime).toLocaleTimeString()
              : "",
            action: a.action
              ? a.action.split("Action:").pop().trim()
              : "",
          }))
        );
      }
    } catch (err) {
      console.error("Activity fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }

  fetchLogs();
}, []);

  /* ---------- filter + sort ---------- */
  const filteredData = useMemo(() => {
    return data
      .filter((r) =>
        Object.values(r).some((val) =>
          (val || "")
            .toString()
            .toLowerCase()
            .includes(debouncedTerm.toLowerCase())
        )
      )
      .sort((a, b) => {
        if (!sortConfig.key) return 0;
        const A = String(a[sortConfig.key] || "").toLowerCase();
        const B = String(b[sortConfig.key] || "").toLowerCase();
        if (A < B) return sortConfig.direction === "asc" ? -1 : 1;
        if (A > B) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
  }, [data, debouncedTerm, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((p) => ({
      key,
      direction: p.key === key && p.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) =>
    sortConfig.key !== key ? (
      <FaSort />
    ) : sortConfig.direction === "asc" ? (
      <FaSortUp />
    ) : (
      <FaSortDown />
    );

  /* ---------- EXPORT TO CSV (same as User page) ---------- */
  const exportToCSV = () => {
    if (!filteredData || filteredData.length === 0) return;

    const csvRows = [];
    const headers = ["Department", "Email", "Date", "Time", "Action"];
    csvRows.push(headers.join(","));

    filteredData.forEach((row) => {
      const values = [
        row.department,
        row.email,
        row.date,
        row.time,
        row.action,
      ].map((val) => `"${val || ""}"`);
      csvRows.push(values.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "activity_log.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ---------- pagination ---------- */
  const indexOfLast = currentPage * rowsPerPage;
  const currentRows = filteredData.slice(
    indexOfLast - rowsPerPage,
    indexOfLast
  );
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  return (
    <div className="activity">
      <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="headSection">
        <div className="compliance-score">Activity Log</div>
      </div>

      {/* Table Header */}
      <div
        className="table-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginLeft: "45px",
          }}
        >
          <button className="btn blue-btn" onClick={() => navigate("/BusinessUnit")}>
            Business Unit
          </button>
          <button className="btn blue-btn" onClick={() => navigate("/department")}>
            Departments
          </button>
          <button className="btn blue-btn" onClick={() => navigate("/user")}>
            Users
          </button>
          <button className="btn gray-btn" onClick={() => navigate("/activity")}>
            Activity
          </button>
        </div>

        {/* RIGHT ACTIONS */}
        <div
          className="table-actions"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginRight: "45px",
          }}
        >
          <div className="search-box">
            <input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="search-icon" />
          </div>

          <button className="action-btn primary" onClick={exportToCSV}>
            Export
          </button>
        </div>
      </div>

      {/* Table */}
      <table className="data-table">
        <thead>
          <tr>
            <th onClick={() => requestSort("department")}>
              Department {getSortIcon("department")}
            </th>
            <th onClick={() => requestSort("email")}>
              Email {getSortIcon("email")}
            </th>
            <th onClick={() => requestSort("date")}>
              Date {getSortIcon("date")}
            </th>
            <th onClick={() => requestSort("time")}>
              Time {getSortIcon("time")}
            </th>
            <th>Action</th>
          </tr>
        </thead>
       <tbody>
  {loading ? (
    <tr>
      <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
        Loading...
      </td>
    </tr>
  ) : currentRows.length > 0 ? (
    currentRows.map((r, i) => (
      <tr key={i}>
        <td><b>{r.department}</b></td>
        <td>{r.email}</td>
        <td>{r.date}</td>
        <td>{r.time}</td>
        <td>{r.action}</td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="5" style={{ textAlign: "center" }}>
        No activity records found
      </td>
    </tr>
  )}
</tbody>
      </table>

      {/* Pagination */}
      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          Prev
        </button>
        <span>
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
